import {
    curatedSearchConfig,
    getFiltersFromRangeState,
    hiddenRefinementsState,
    type CuratedSearchConfig,
} from "@/services/algoliaRouting.blade";

/** Ce qu'on lit de l'état de recherche (`SearchParameters`) des résultats affichés. */
export interface MakeMenuSearchState {
    query?: string;
    facetsRefinements?: Record<string, string[]>;
    disjunctiveFacetsRefinements?: Record<string, string[]>;
    hierarchicalFacetsRefinements?: Record<string, string[]>;
}

/** Marques imposées par la page (landing marque, page dynamique, recherche curée). */
export function pageImposedMakes(
    config: CuratedSearchConfig = curatedSearchConfig.value,
): string[] {
    const filters = config.isActive ? config.filters : undefined;
    if (!filters) return [];

    return [
        ...new Set(
            [filters.menu?.make, ...(filters.refinementList?.make ?? [])].filter(
                (value): value is string => typeof value === "string" && value !== "",
            ),
        ),
    ];
}

const hasValuesBesidesMake = (refinements?: Record<string, unknown>): boolean =>
    Object.entries(refinements ?? {}).some(
        ([attribute, values]) =>
            attribute !== "make" &&
            (Array.isArray(values) ? values.length > 0 : values != null && values !== ""),
    );

/**
 * Vrai quand la recherche est restreinte par autre chose que la marque (plage de prix ou de
 * mensualité, carburant, modèle, texte…). Les compteurs servis avec la page sont ceux de tout
 * le stock : ils ne valent alors plus pour les autres marques.
 */
export function hasSearchConstraintBesidesMake(
    state?: MakeMenuSearchState | null,
    config: CuratedSearchConfig = curatedSearchConfig.value,
): boolean {
    const filters = config.isActive ? config.filters : undefined;
    if (filters) {
        const { menu, refinementList, ...others } = filters;
        if (hasValuesBesidesMake(menu) || hasValuesBesidesMake(refinementList)) return true;
        if (Object.values(others).some((value) => value != null && value !== false)) {
            return true;
        }
    }

    if (getFiltersFromRangeState() !== "") return true;
    if (Object.values(hiddenRefinementsState.value).some((values) => values.length > 0)) {
        return true;
    }

    if (!state) return false;
    if (state.query?.trim()) return true;

    return (
        hasValuesBesidesMake(state.facetsRefinements) ||
        hasValuesBesidesMake(state.disjunctiveFacetsRefinements) ||
        hasValuesBesidesMake(state.hierarchicalFacetsRefinements)
    );
}

export interface ContextMakeItem {
    value: string;
    label: string;
    count: number;
    isRefined: boolean;
}

/**
 * Marques de la liste quand la page est contrainte : celles du contexte, avec leur compteur. La
 * marque choisie ou imposée reste affichée même sans véhicule dans ce contexte (page vide), pour
 * garder sa croix.
 */
export function buildContextMakeItems(
    items: ReadonlyArray<{ value: string; label: string; count: number; isRefined: boolean }>,
    selectedMake?: string,
): ContextMakeItem[] {
    const list = items.map((item) => ({
        value: item.value,
        label: item.label,
        count: item.count,
        isRefined: selectedMake ? item.value === selectedMake : item.isRefined,
    }));
    if (selectedMake && !list.some((item) => item.value === selectedMake)) {
        list.push({ value: selectedMake, label: selectedMake, count: 0, isRefined: true });
    }

    // Même ordre que la liste servie avec la page (sans tenir compte de la casse).
    return list.sort((a, b) => a.label.localeCompare(b.label, "fr", { sensitivity: "base" }));
}
